import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import 'src/pages/main/house/drag.css'

const PLANES_ORDER = ['left', 'right', 'floor']
const ITEM_PIXELS_PER_TILE = 300
const ITEM_ANCHOR_X = 50
const ITEM_ANCHOR_Y = 70
const WALL_ITEM_ANCHOR_Y = 50

const WALL_CONFIGS = {
  8: { left: { x: -16, y: 36, z: 116 }, right: { x: -203, y: 133, z: 106 } },
  10: { left: { x: -6, y: 36, z: 155 }, right: { x: -220, y: 122, z: 106 } },
  12: { left: { x: 3, y: 36, z: 194 }, right: { x: -236, y: 112, z: 106 } }
}

const getWallTransforms = (gridSize) => {
  const { left: l, right: r } = WALL_CONFIGS[gridSize] || WALL_CONFIGS[8]
  return {
    left: `scale(1.15) rotateX(30deg) rotateY(308deg) rotateZ(0deg) translateY(${l.y}px) translateX(${l.x}px) translateZ(${l.z}px)`,
    right: `scale(1.15) rotateX(30deg) rotateY(412deg) rotateZ(0deg) translateZ(${r.z}px) translateX(${r.x}px) translateY(${r.y}px)`
  }
}

const getOverlaySize = (item) => {
  const width = item.itemWidth || 1
  const height = item.itemHeight || 1

  if (item.reverse_flag) {
    return { width: height, height: width }
  }

  return { width, height }
}

const doGridAreasOverlap = (areaA, areaB) =>
  areaA.startX < areaB.startX + areaB.itemWidth &&
  areaA.startX + areaA.itemWidth > areaB.startX &&
  areaA.startY < areaB.startY + areaB.itemHeight &&
  areaA.startY + areaA.itemHeight > areaB.startY

const getOverlayPositionKey = (plane, startX, startY) => `${plane}-${startX}-${startY}`

const isWallPlane = (plane) => plane === 'left' || plane === 'right'

const getItemOverlayTransform = (plane) => {
  const anchorYPercent = isWallPlane(plane) ? WALL_ITEM_ANCHOR_Y : ITEM_ANCHOR_Y

  return `translateX(-${ITEM_ANCHOR_X}%) translateY(-${anchorYPercent}%)`
}

const getGhostOverlayTransform = (plane) => {
  const anchorYPercent = isWallPlane(plane) ? WALL_ITEM_ANCHOR_Y : ITEM_ANCHOR_Y

  return `translateX(-${ITEM_ANCHOR_X}%) translateY(calc(-${anchorYPercent}% - 12px))`
}

const getItemImageSrc = (item) => item.displayImage || item.icon_url

const getContainerScale = (container) => {
  const style = window.getComputedStyle(container)
  const matrix = new DOMMatrix(style.transform)

  return {
    scaleX: matrix.a || 1,
    scaleY: matrix.d || 1
  }
}

const calcTileVisualWidth = (container, plane) => {
  const tile = container.querySelector(
    `[data-house-tile="true"][data-plane="${plane}"][data-grid-x="0"][data-grid-y="0"]`
  )

  if (!tile) return null

  const { scaleX } = getContainerScale(container)
  const tileRect = tile.getBoundingClientRect()

  return tileRect.width / scaleX
}

const calcGridFootprintAnchor = (container, plane, startX, startY, itemWidth, itemHeight) => {
  const containerRect = container.getBoundingClientRect()
  const { scaleX, scaleY } = getContainerScale(container)
  const bounds = {
    left: Infinity,
    top: Infinity,
    right: -Infinity,
    bottom: -Infinity
  }

  for (let dx = 0; dx < itemWidth; dx++) {
    for (let dy = 0; dy < itemHeight; dy++) {
      const tile = container.querySelector(
        `[data-house-tile="true"][data-plane="${plane}"]` +
          `[data-grid-x="${startX + dx}"][data-grid-y="${startY + dy}"]`
      )

      if (!tile) continue

      const tileRect = tile.getBoundingClientRect()
      bounds.left = Math.min(bounds.left, tileRect.left)
      bounds.top = Math.min(bounds.top, tileRect.top)
      bounds.right = Math.max(bounds.right, tileRect.right)
      bounds.bottom = Math.max(bounds.bottom, tileRect.bottom)
    }
  }

  if (!Number.isFinite(bounds.left)) return null

  return {
    x: ((bounds.left + bounds.right) / 2 - containerRect.left) / scaleX,
    y: ((bounds.top + bounds.bottom) / 2 - containerRect.top) / scaleY
  }
}

const areOverlayPositionsEqual = (a, b) => {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)

  if (aKeys.length !== bKeys.length) return false

  return aKeys.every((key) => a[key]?.x === b[key]?.x && a[key]?.y === b[key]?.y)
}

const arePlaneTileWidthsEqual = (a, b) => PLANES_ORDER.every((plane) => a[plane] === b[plane])

// Kahn's Algorithm: topological sort để xác định thứ tự render đúng trong isometric view
// A nằm SAU B nếu: A.maxX <= B.minX HOẶC A.maxY <= B.minY
const topoSortItems = (items) => {
  const n = items.length
  if (n <= 1) return items

  // Bước 1: Build "behind" graph - edges[i] = danh sách index của item phải render SAU item i
  const inDegree = new Array(n).fill(0)
  const edges = Array.from({ length: n }, () => [])

  for (let i = 0; i < n; i++) {
    const a = items[i]
    const aMinX = a.startX
    const aMaxX = a.startX + (a.itemWidth || 1)
    const aMinY = a.startY
    const aMaxY = a.startY + (a.itemHeight || 1)

    for (let j = 0; j < n; j++) {
      if (i === j) continue
      const b = items[j]
      const bMinX = b.startX
      const bMaxX = b.startX + (b.itemWidth || 1)
      const bMinY = b.startY
      const bMaxY = b.startY + (b.itemHeight || 1)

      // Kiểm tra A nằm sau B (A phải render trước B)
      const aBehindB = aMaxX <= bMinX || aMaxY <= bMinY
      // Kiểm tra B nằm sau A (B phải render trước A)
      const bBehindA = bMaxX <= aMinX || bMaxY <= aMinY

      if (aBehindB && !bBehindA) {
        // A sau B → A render trước B → edge: i → j
        edges[i].push(j)
        inDegree[j]++
      }
    }
  }

  // Bước 2: Kahn's Algorithm
  const queue = []
  for (let i = 0; i < n; i++) {
    if (inDegree[i] === 0) queue.push(i)
  }

  const result = []
  while (queue.length > 0) {
    const idx = queue.shift()
    result.push(items[idx])
    for (const neighbor of edges[idx]) {
      inDegree[neighbor]--
      if (inDegree[neighbor] === 0) queue.push(neighbor)
    }
  }

  // Fallback nếu có cycle: append các item chưa được xử lý, sort theo depth
  if (result.length < n) {
    const inResult = new Set(result.map((item) => item.startX + '-' + item.startY))
    const remaining = items
      .filter((item) => !inResult.has(item.startX + '-' + item.startY))
      .sort((a, b) => {
        const dA = a.startX + (a.itemWidth - 1) / 2 + a.startY + (a.itemHeight - 1) / 2
        const dB = b.startX + (b.itemWidth - 1) / 2 + b.startY + (b.itemHeight - 1) / 2
        return dA - dB
      })
    result.push(...remaining)
  }

  return result
}

export const HouseGrid3D = ({
  containerRef,
  isPinching,
  gridSize,
  wallRows,
  tileSize,
  size,
  wallHeight,
  isLayoutMode,
  justDroppedKey,
  hoverPosition,
  draggingItem,
  ghostItem,
  getItemAtPosition,
  getItemsForPlane,
  isTileHovered,
  onItemClick,
  onGhostPointerDown,
  floorBackgroundImage,
  leftWallBackgroundImage,
  rightWallBackgroundImage
}) => {
  const wallTransforms = useMemo(() => getWallTransforms(gridSize), [gridSize])
  const [overlayPositions, setOverlayPositions] = useState({})
  const [planeTileVisualWidths, setPlaneTileVisualWidths] = useState({})
  const [imageNaturalSizes, setImageNaturalSizes] = useState({})

  const handleOverlayImageLoad = useCallback((src, event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget
    if (!src || !naturalWidth || !naturalHeight) return

    setImageNaturalSizes((prev) => {
      const current = prev[src]
      if (current?.width === naturalWidth && current?.height === naturalHeight) return prev
      return { ...prev, [src]: { width: naturalWidth, height: naturalHeight } }
    })
  }, [])

  const getOverlayPixelWidth = useCallback(
    (item, fallbackWidth, plane) => {
      const src = getItemImageSrc(item)
      const naturalSize = imageNaturalSizes[src]
      const tilePixelWidth = isWallPlane(plane)
        ? planeTileVisualWidths[plane] || tileSize
        : tileSize

      if (!naturalSize?.width) return fallbackWidth * tilePixelWidth

      return (naturalSize.width / ITEM_PIXELS_PER_TILE) * tilePixelWidth
    },
    [imageNaturalSizes, planeTileVisualWidths, tileSize]
  )

  const recalculatePositions = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const nextPositions = {}
    const nextPlaneTileVisualWidths = {}

    PLANES_ORDER.forEach((plane) => {
      const tileVisualWidth = calcTileVisualWidth(container, plane)
      if (tileVisualWidth) nextPlaneTileVisualWidths[plane] = tileVisualWidth

      getItemsForPlane(plane).forEach((itemData) => {
        const { startX, startY } = itemData
        const itemWidth = itemData.itemWidth || 1
        const itemHeight = itemData.itemHeight || 1
        const position = calcGridFootprintAnchor(
          container,
          plane,
          startX,
          startY,
          itemWidth,
          itemHeight
        )

        if (position) nextPositions[getOverlayPositionKey(plane, startX, startY)] = position
      })
    })

    if (ghostItem) {
      const itemWidth = ghostItem.itemWidth || 1
      const itemHeight = ghostItem.itemHeight || 1
      const position = calcGridFootprintAnchor(
        container,
        ghostItem.plane,
        ghostItem.startX,
        ghostItem.startY,
        itemWidth,
        itemHeight
      )

      if (position) nextPositions[`ghost-${ghostItem.plane}`] = position
    }

    setOverlayPositions((prev) =>
      areOverlayPositionsEqual(prev, nextPositions) ? prev : nextPositions
    )
    setPlaneTileVisualWidths((prev) =>
      arePlaneTileWidthsEqual(prev, nextPlaneTileVisualWidths) ? prev : nextPlaneTileVisualWidths
    )
  }, [containerRef, ghostItem, getItemsForPlane, gridSize, tileSize, wallRows])

  const recalculatePositionsRef = useRef(recalculatePositions)

  useEffect(() => {
    recalculatePositionsRef.current = recalculatePositions
  }, [recalculatePositions])

  useLayoutEffect(() => {
    recalculatePositions()
  }, [recalculatePositions])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleResize = () => {
      recalculatePositionsRef.current()
    }
    window.addEventListener('resize', handleResize)

    const resizeObserver = new ResizeObserver(() => {
      recalculatePositionsRef.current()
    })
    resizeObserver.observe(container)

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
    }
  }, [containerRef])

  const getTileClassName = (plane, x, y, baseColor) => {
    const itemData = getItemAtPosition(plane, x, y)
    const hasItem = !!itemData
    const isHovered = isTileHovered(plane, x, y)

    const hasBackground =
      (plane === 'floor' && floorBackgroundImage) ||
      (plane === 'left' && leftWallBackgroundImage) ||
      (plane === 'right' && rightWallBackgroundImage)
    const tileColor = hasBackground ? 'bg-transparent' : baseColor

    const isInvalidPlane = (() => {
      if (!draggingItem || !isLayoutMode) return false
      const itemSpace = draggingItem.individual_space || 'floor'
      const isWallItem = ['wall', 'left', 'right'].includes(itemSpace)
      return isWallItem ? plane === 'floor' : plane !== 'floor'
    })()

    const inGhostRange =
      ghostItem?.plane === plane &&
      x >= ghostItem.startX &&
      x < ghostItem.startX + ghostItem.itemWidth &&
      y >= ghostItem.startY &&
      y < ghostItem.startY + ghostItem.itemHeight

    const inHoverRange =
      draggingItem?.isGhost &&
      hoverPosition?.plane === plane &&
      x >= hoverPosition.startX &&
      x < hoverPosition.startX + (hoverPosition.itemWidth || 1) &&
      y >= hoverPosition.startY &&
      y < hoverPosition.startY + (hoverPosition.itemHeight || 1)

    if (isInvalidPlane) return 'bg-gray-400 opacity-60'
    if (inGhostRange && hasItem) return 'bg-red-400 opacity-70'
    if (inGhostRange) return 'bg-yellow-200 opacity-60'
    if (inHoverRange && hasItem) return 'bg-red-400 opacity-70'
    if (inHoverRange) return 'bg-yellow-300 opacity-70'
    if (hasItem && isLayoutMode) return 'bg-gray-400'
    if (isHovered) return 'bg-yellow-300 opacity-70'
    return tileColor
  }

  const renderTile = (plane, x, y, baseColor) => (
    <div
      key={`${plane}-${x}-${y}`}
      data-house-tile="true"
      data-plane={plane}
      data-grid-x={x}
      data-grid-y={y}
      className={`${isLayoutMode ? 'border border-white' : ''} ${getTileClassName(plane, x, y, baseColor)}`}
      style={{ width: tileSize, height: tileSize, pointerEvents: 'auto' }}
    />
  )

  const renderItemOverlayLayer = () => {
    const overlays = []

    PLANES_ORDER.forEach((plane) => {
      const items = getItemsForPlane(plane)
      const sorted = topoSortItems(items)

      sorted.forEach((itemData, renderIndex) => {
        const { startX, startY } = itemData
        const w = itemData.itemWidth || 1
        const h = itemData.itemHeight || 1
        const { width: overlayWidth } = getOverlaySize(itemData)
        const imageSrc = getItemImageSrc(itemData)
        const centerKey = `${plane}-${startX + Math.floor(w / 2)}-${startY + Math.floor(h / 2)}`
        const isJustDropped = centerKey === justDroppedKey
        const position = overlayPositions[getOverlayPositionKey(plane, startX, startY)]

        if (!position) return

        const isOverlappedByDroppedGhost =
          isLayoutMode &&
          ghostItem?.plane === plane &&
          !draggingItem?.isGhost &&
          doGridAreasOverlap(
            { startX, startY, itemWidth: w, itemHeight: h },
            {
              startX: ghostItem.startX,
              startY: ghostItem.startY,
              itemWidth: ghostItem.itemWidth || 1,
              itemHeight: ghostItem.itemHeight || 1
            }
          )

        const isBeingMoved =
          ghostItem?.originalPosition?.plane === plane &&
          ghostItem?.originalPosition?.startX === startX &&
          ghostItem?.originalPosition?.startY === startY
        if (isBeingMoved) return

        overlays.push(
          <div
            key={`item-${plane}-${startX}-${startY}`}
            onClick={() => isLayoutMode && onItemClick?.(plane, startX, startY, itemData)}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              position: 'absolute',
              left: position.x,
              top: position.y,
              width: getOverlayPixelWidth(itemData, overlayWidth, plane),
              transform: getItemOverlayTransform(plane),
              zIndex: (plane === 'floor' ? 200 : 0) + renderIndex + 1,
              opacity: isOverlappedByDroppedGhost ? 0.5 : 1,
              pointerEvents: isLayoutMode && !draggingItem?.isGhost ? 'auto' : 'none',
              cursor: isLayoutMode ? 'pointer' : 'default'
            }}
          >
            <img
              draggable={false}
              src={imageSrc}
              alt={itemData.name || 'Item'}
              onLoad={(event) => handleOverlayImageLoad(imageSrc, event)}
              className={`ios-media-guard pointer-events-none ${isJustDropped ? 'animate-bounce' : ''}`}
              style={{ display: 'block', width: '100%', height: 'auto', objectFit: 'contain' }}
            />
          </div>
        )
      })
    })

    if (ghostItem) {
      const { width: overlayWidth } = getOverlaySize(ghostItem)
      const imageSrc = getItemImageSrc(ghostItem)
      const position = overlayPositions[`ghost-${ghostItem.plane}`]

      if (!position) return overlays

      overlays.push(
        <div
          key={`ghost-${ghostItem.plane}`}
          onPointerDown={onGhostPointerDown}
          onContextMenu={(e) => e.preventDefault()}
          className="drag-item ios-media-guard cursor-grab active:cursor-grabbing"
          style={{
            position: 'absolute',
            left: position.x,
            top: position.y,
            width: getOverlayPixelWidth(ghostItem, overlayWidth, ghostItem.plane),
            transform: getGhostOverlayTransform(ghostItem.plane),
            zIndex: 999,
            pointerEvents: draggingItem?.isGhost ? 'none' : 'auto'
          }}
        >
          <img
            draggable={false}
            src={imageSrc}
            alt={ghostItem.name || 'Ghost'}
            onLoad={(event) => handleOverlayImageLoad(imageSrc, event)}
            className="ios-media-guard pointer-events-none"
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              opacity: 0.5
            }}
          />
        </div>
      )
    }

    return overlays
  }

  const renderGrid = ({
    plane,
    cols,
    rows,
    baseColor,
    borderClass,
    transform,
    bg,
    wrapperStyle
  }) => (
    <div className="absolute" style={wrapperStyle}>
      <div
        className={`grid ${borderClass} relative box-border`}
        style={{
          width: plane === 'floor' ? size : size,
          height: plane === 'floor' ? size : wallHeight,
          gridTemplateColumns: `repeat(${cols}, ${tileSize}px)`,
          gridTemplateRows: `repeat(${rows}, ${tileSize}px)`,
          transform,
          transformOrigin: 'center center',
          display: 'grid',
          pointerEvents: 'none',
          position: 'relative',
          backgroundImage: bg ? `url(${bg})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {[...Array(cols * rows)].map((_, i) =>
          renderTile(plane, i % cols, Math.floor(i / cols), baseColor)
        )}
      </div>
    </div>
  )

  return (
    <div
      ref={containerRef}
      className={`ios-media-guard relative z-10 ${isLayoutMode ? 'top-[20%]' : ''}`}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        width: '100%',
        height: '100%',
        transformOrigin: 'center center',
        transition: isPinching ? 'none' : 'transform 0.1s ease-out',
        touchAction: 'none',
        willChange: 'transform'
      }}
    >
      {renderGrid({
        plane: 'floor',
        cols: gridSize,
        rows: gridSize,
        baseColor: 'bg-purple-500',
        borderClass: 'border-r-[10px] border-b-[10px] border-gray-500',
        transform: 'rotateX(50deg) rotateZ(45deg)',
        bg: floorBackgroundImage,
        wrapperStyle: {
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          willChange: 'transform',
          zIndex: 3
        }
      })}

      {renderGrid({
        plane: 'left',
        cols: gridSize,
        rows: wallRows,
        baseColor: 'bg-sky-400',
        borderClass: 'border-l-[10px] border-t-[10px] border-gray-500',
        transform: wallTransforms.left,
        bg: leftWallBackgroundImage,
        wrapperStyle: {
          left: '50%',
          top: 'calc(50% - 180px)',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          willChange: 'transform',
          zIndex: 2
        }
      })}

      {renderGrid({
        plane: 'right',
        cols: gridSize,
        rows: wallRows,
        baseColor: 'bg-sky-700',
        borderClass: 'border-r-[10px] border-t-[10px] border-gray-500',
        transform: wallTransforms.right,
        bg: rightWallBackgroundImage,
        wrapperStyle: {
          top: 'calc(50% - 180px)',
          right: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          willChange: 'transform',
          zIndex: 1
        }
      })}

      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 30,
          transformOrigin: 'top left'
        }}
      >
        {renderItemOverlayLayer()}
      </div>
    </div>
  )
}

HouseGrid3D.propTypes = {
  containerRef: PropTypes.object.isRequired,
  isPinching: PropTypes.bool.isRequired,
  gridSize: PropTypes.number.isRequired,
  wallRows: PropTypes.number.isRequired,
  tileSize: PropTypes.number.isRequired,
  size: PropTypes.number.isRequired,
  wallHeight: PropTypes.number.isRequired,
  isLayoutMode: PropTypes.bool.isRequired,
  justDroppedKey: PropTypes.string,
  hoverPosition: PropTypes.object,
  draggingItem: PropTypes.object,
  ghostItem: PropTypes.object,
  getItemAtPosition: PropTypes.func.isRequired,
  getItemsForPlane: PropTypes.func.isRequired,
  isTileHovered: PropTypes.func.isRequired,
  onItemClick: PropTypes.func,
  onGhostPointerDown: PropTypes.func,
  floorBackgroundImage: PropTypes.string,
  leftWallBackgroundImage: PropTypes.string,
  rightWallBackgroundImage: PropTypes.string
}
